import { FiCheckSquare } from 'react-icons/fi';

export const tfngBasicsData = {
  courseId: 'tfng-basics',
  courseType: 'reading-course',
  title: 'True/False/Not Given Basics',
  icon: FiCheckSquare,
  overview: {
    impact: 'Make precise evidence-based decisions under time pressure',
    focus: 'Separating direct support, contradiction, and missing information',
    uses: 'Reading accuracy, paraphrase recognition, evidence tracking',
    duration: '25-30 minutes',
  },
  sections: [
    {
      title: 'What IELTS Is Really Testing',
      content:
        '<p>True/False/Not Given is not a vocabulary quiz. It tests whether you can compare a statement with a passage <strong>without adding your own logic</strong>.</p>',
      points: [
        '<strong>TRUE</strong>: the statement matches the passage meaning.',
        '<strong>FALSE</strong>: the statement contradicts the passage meaning.',
        '<strong>NOT GIVEN</strong>: the passage does not provide enough information to decide.',
        'Judge the statement against the text, not against your background knowledge.',
      ],
      warning:
        "The most common mistake is choosing FALSE when the text simply doesn't say enough.",
    },
    {
      title: 'Decision Rules',
      content: '<p>Use the same sequence every time:</p>',
      points: [
        'Find the exact part of the passage the statement refers to.',
        'Check whether the meaning matches, contradicts, or stays unanswered.',
        'Pay special attention to numbers, frequency words, and scope words such as some, many, all, only, and most.',
        'If you still need to infer a missing fact, the answer is usually <strong>NOT GIVEN</strong>.',
      ],
      examples: [
        {
          label: 'TRUE',
          text: 'Passage: "The bridge opened in 2008." Statement: "The bridge began operating in 2008."',
        },
        {
          label: 'FALSE',
          text: 'Passage: "Only 18% of residents cycled to work." Statement: "Most residents cycled to work."',
        },
        {
          label: 'NOT GIVEN',
          text: 'Passage: "The library was renovated in 2021." Statement: "The renovation cost more than expected."',
        },
      ],
    },
    {
      title: 'High-Risk Trap Types',
      content: '<p>IELTS often hides the answer inside small but important meaning shifts:</p>',
      table: {
        headers: ['Trap', 'What to Watch', 'Likely Outcome'],
        rows: [
          ['Numbers', '18% vs most / 2 years vs several years', 'Often FALSE'],
          ['Frequency', 'sometimes vs always / rarely vs often', 'Often FALSE'],
          ['Scope', 'some students vs all students', 'Often FALSE'],
          ['Cause', 'A followed B vs A caused B', 'Often FALSE or NOT GIVEN'],
          ['Missing detail', 'topic is mentioned but the key detail is absent', 'NOT GIVEN'],
        ],
      },
    },
    {
      title: 'A Reliable Workflow',
      content: '<p>Use this routine under timed conditions:</p>',
      points: [
        'Read the statement carefully and mark the main claim.',
        'Scan for the matching paragraph using names, dates, or topic nouns.',
        'Read 2-3 lines around the evidence, not just one sentence.',
        'Decide by meaning, not by matching surface words.',
        'If the writer never actually states the missing idea, do not invent it.',
      ],
    },
  ],
  exercises: [
    {
      title: 'Core Classification Drill',
      type: 'select',
      instructions: 'Read the passage extract and choose True, False, or Not Given.',
      questions: [
        {
          text:
            'Passage: "The city introduced electric buses in 2022. In the first year, fuel costs fell by 18%, although passenger numbers remained stable."\nStatement: "The number of passengers increased after electric buses were introduced."',
          options: ['True', 'False', 'Not Given'],
          answer: 'False',
          explanation:
            'The passage says passenger numbers remained stable, so "increased" contradicts the text.',
        },
        {
          text:
            'Passage: "Researchers surveyed 340 teachers across 12 schools. Most participants said they used digital homework platforms at least once a week."\nStatement: "The survey included teachers from more than ten schools."',
          options: ['True', 'False', 'Not Given'],
          answer: 'True',
          explanation: 'Twelve schools means more than ten schools.',
        },
        {
          text:
            'Passage: "The museum reopened after a six-month restoration project. Visitors particularly praised the redesigned entrance hall."\nStatement: "The restoration cost less than originally planned."',
          options: ['True', 'False', 'Not Given'],
          answer: 'Not Given',
          explanation:
            'The passage discusses the restoration and visitor response, but says nothing about the budget.',
        },
        {
          text:
            'Passage: "Some students preferred recorded lectures because they could pause them. Others still chose live classes for the chance to ask questions."\nStatement: "All students preferred recorded lectures."',
          options: ['True', 'False', 'Not Given'],
          answer: 'False',
          explanation:
            'The passage clearly says some students still chose live classes, so "all" is contradicted.',
        },
      ],
    },
    {
      title: 'Spot the Trap',
      type: 'select',
      instructions: 'Choose the best description of the problem in each statement.',
      questions: [
        {
          text:
            'Passage: "The farm reduced water use by installing sensors in the largest fields."\nStatement: "The farm reduced water use in every field."',
          options: ['Contradiction of scope', 'Missing information', 'Paraphrase match'],
          answer: 'Contradiction of scope',
          explanation:
            'The passage limits the action to the largest fields, not every field.',
        },
        {
          text:
            'Passage: "The guidebook recommends visiting the castle early in the morning."\nStatement: "The guidebook says the castle is closed in the afternoon."',
          options: ['Contradiction of scope', 'Missing information', 'Paraphrase match'],
          answer: 'Missing information',
          explanation:
            'A recommendation to go early does not tell us whether it is closed later.',
        },
        {
          text:
            'Passage: "The team expanded from 8 engineers to 15 by the end of the year."\nStatement: "The engineering team almost doubled in size."',
          options: ['Contradiction of scope', 'Missing information', 'Paraphrase match'],
          answer: 'Paraphrase match',
          explanation:
            'Going from 8 to 15 is a reasonable paraphrase of "almost doubled".',
        },
      ],
    },
    {
      title: 'Choose the Evidence',
      type: 'select',
      instructions: 'Select the sentence that gives the strongest evidence for the answer.',
      questions: [
        {
          text:
            'Statement: "The company launched the app before testing it with users."\nWhich evidence matters most?',
          options: [
            '"Beta trials with 500 users were completed in March before the public launch in May."',
            '"The company hired more designers in February."',
            '"The public downloaded the app 20,000 times in June."',
          ],
          answer:
            '"Beta trials with 500 users were completed in March before the public launch in May."',
          explanation:
            'This sentence directly answers the timing question and shows testing happened before launch.',
        },
        {
          text:
            'Statement: "The article says tourism is the village\'s only source of income."\nWhich evidence matters most?',
          options: [
            '"Many visitors arrive in summer."',
            '"In addition to tourism, residents earn money from fishing and olive farming."',
            '"The village has become popular with photographers."',
          ],
          answer:
            '"In addition to tourism, residents earn money from fishing and olive farming."',
          explanation:
            'This sentence directly contradicts the word "only".',
        },
      ],
    },
    {
      title: 'Paraphrase to Question Match',
      type: 'fill-in',
      instructions: 'Type the correct answer: True, False, or Not Given.',
      questions: [
        {
          text:
            'Passage: "The report noted a slight decline in rainfall over the decade."\nStatement: "Rainfall dropped a little over ten years."',
          answer: 'True',
          explanation:
            '"Slight decline" paraphrases "dropped a little" and "over the decade" means "over ten years".',
        },
        {
          text:
            'Passage: "The school introduced coding lessons for Year 8 students."\nStatement: "Year 7 students also received coding lessons."',
          answer: 'Not Given',
          explanation:
            'The passage tells us about Year 8 only. It does not confirm or deny Year 7.',
        },
        {
          text:
            'Passage: "Only a minority of applicants had previous management experience."\nStatement: "Most applicants had worked in management before."',
          answer: 'False',
          explanation:
            '"Only a minority" directly opposes "most".',
        },
      ],
    },
    {
      title: 'Explain Your Decision',
      type: 'writing',
      instructions: 'Write one or two sentences explaining why the answer is NOT GIVEN or FALSE.',
      questions: [
        {
          text:
            'Passage: "The lecture theatre was renovated last summer, and students commented that the sound quality improved."\nStatement: "The renovation was completed ahead of schedule."\nWhy is this NOT GIVEN?',
          example:
            'It is NOT GIVEN because the passage talks about when the renovation happened and its result, but it never mentions the planned schedule.',
          tip:
            'A strong explanation points out exactly which key detail is missing.',
        },
        {
          text:
            'Passage: "The article states that online sales rose rapidly, while in-store sales stayed almost unchanged."\nStatement: "In-store sales fell sharply."\nWhy is this FALSE?',
          example:
            'It is FALSE because the passage says in-store sales stayed almost unchanged, which contradicts the idea that they fell sharply.',
          tip: 'For FALSE, name the exact contradiction.',
        },
      ],
    },
  ],
  tips: [
    'Circle absolutes like all, only, always, never, completely.',
    'Treat opinion, cause, and comparison statements with extra care.',
    'When in doubt, ask: did the writer actually say this, or did I infer it?',
    'Review every NOT GIVEN answer to check whether it was truly missing or just hard to find.',
  ],
  dailyPractice: [
    'Take 5 statements from an article and classify them as supported, contradicted, or unconfirmed.',
    'Rewrite one TRUE statement into a FALSE version by changing scope or number words.',
    'Practice spotting paraphrases between questions and short passages.',
    'Review one TFNG set and write why each wrong option was wrong.',
  ],
  backPath: '/exams/english/#reading',
  backLabel: 'Back to Reading',
  aiContext:
    'True/False/Not Given Basics: judging statements against evidence, spotting contradiction, and recognizing when information is missing rather than false.',
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...tfngBasicsData,
      title: tfngBasicsData.title + ' (Foundations)',
      overview: { ...tfngBasicsData.overview, duration: '15-20 minutes' },
      sections: tfngBasicsData.sections.slice(0, 2),
      exercises: [
        {
          ...tfngBasicsData.exercises[0],
          title: 'Core Classification Drill (Basic)',
          questions: tfngBasicsData.exercises[0].questions.slice(0, 3),
        },
        {
          ...tfngBasicsData.exercises[3],
          title: 'Paraphrase to Question Match (Basic)',
          questions: tfngBasicsData.exercises[3].questions.slice(0, 2),
        },
      ],
      tips: tfngBasicsData.tips.slice(0, 3),
    };
  }
  if (difficulty >= 4) {
    return {
      ...tfngBasicsData,
      title: tfngBasicsData.title + ' (Advanced)',
      overview: { ...tfngBasicsData.overview, duration: '30-35 minutes' },
      sections: [
        ...tfngBasicsData.sections,
        {
          title: 'Advanced Inference Checks',
          content:
            '<p>At higher bands, IELTS uses softer language and embedded opinion. The answer often depends on noticing whether the writer <strong>states</strong> an evaluation or merely reports someone else\'s view.</p>',
          points: [
            'Separate the writer\'s voice from quoted expert opinions.',
            'Watch hedging language like may, appears to, is likely to.',
            'Be careful with stronger paraphrases such as proves, guarantees, or completely.',
          ],
        },
      ],
      exercises: [
        ...tfngBasicsData.exercises.map((ex) => ({
          ...ex,
          title: ex.title + ' (Advanced)',
        })),
        {
          title: 'Advanced Evidence Judgement',
          type: 'select',
          instructions: 'Choose the best answer for each nuanced statement.',
          questions: [
            {
              text:
                'Passage: "Several analysts suggested the policy may reduce emissions over time, although no long-term data is yet available."\nStatement: "The passage proves the policy will reduce emissions."',
              options: ['True', 'False', 'Not Given'],
              answer: 'False',
              explanation:
                'The passage says analysts suggested it may reduce emissions; that is weaker than proving it will.',
            },
            {
              text:
                'Passage: "Dr Shah argued that urban farming should be supported, but the report itself remained neutral on the issue."\nStatement: "The report supports urban farming."',
              options: ['True', 'False', 'Not Given'],
              answer: 'False',
              explanation:
                'The report is described as neutral, so it does not support the policy.',
            },
          ],
        },
      ],
      tips: [
        ...(tfngBasicsData.tips || []),
        'At Band 8+, compare strength of meaning, not just topic overlap.',
        'Write down why an answer is NOT GIVEN before checking explanations; this improves discipline.',
      ],
    };
  }
  return tfngBasicsData;
}
